import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { technicianMfiApi } from "./api";

const COLORS = {
  aquaBlue: "#11a8a4",
  grassGreen: "#7ed957",
  darkGrey: "#3c3b3f",
  vividPurple: "#9440dd",
  white: "#ffffff",
  lightGrey: "#f3f4f6",
  mediumGrey: "#e5e7eb",
  darkText: "#1f2937",
  mediumText: "#6b7280",
  lightText: "#9ca3af",
};

// Params that must use Normal / Abnormal dropdown instead of free-text input.
// Matching is case-insensitive and tolerant to extra spaces around hyphens.
const NORMAL_ABNORMAL_EXACT = new Set(
  [
    "ecg",
    // "waist circumference",
    // "waist-circumference",
    // "hip circumference",
    // "hip-circumference",
    // "mid arm circumference",
    // "mid-arm circumference",
    "astigmatism-left",
    "astigmatism-right",
    "duochrome-right",
    "duochrome-left",
    "duo chrome-right",
    "duo chrome-left",
    "color bindness-left",
    "color bindness-right",
    "color blindness-left",
    "color blindness-right",
    "colour blindness-left",
    "colour blindness-right",
    "hba1c",
    "hb a1c",
    "hb-a1c",
  ].map((s) => s.toLowerCase())
);

function normalizeParamName(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\s*-\s*/g, "-");
}


  function extractDentalPredictions(response) {
    const predictions = [];

    const labelKeys = [
      "predicted_class",
      "predicted_label",
      "class_name",
      "disease",
      "prediction",
      "label",
      "class",
    ];

    const confidenceKeys = [
      "confidence",
      "confidence_score",
      "probability",
      "score",
    ];

    function visit(obj, source = "Dental AI", depth = 0) {
      if (obj == null || depth > 8) return;

      if (Array.isArray(obj)) {
        obj.forEach((item, index) =>
          visit(item, `${source} ${index + 1}`, depth + 1)
        );
        return;
      }

      if (typeof obj !== "object") return;

      const labelKey = labelKeys.find(
        (key) =>
          typeof obj[key] === "string" &&
          obj[key].trim() !== ""
      );

      if (labelKey) {
        const confidenceKey = confidenceKeys.find(
          (key) =>
            obj[key] != null &&
            Number.isFinite(Number(obj[key]))
        );

        let confidence = confidenceKey
          ? Number(obj[confidenceKey])
          : null;

        if (confidence !== null && confidence <= 1) {
          confidence *= 100;
        }

        predictions.push({
          label: obj[labelKey].trim(),
          confidence,
          source,
        });
      }

      Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === "object") {
          visit(value, key, depth + 1);
        }
      });
    }

    visit(response);

    return predictions.filter(
      (item, index, array) =>
        array.findIndex(
          (other) =>
            other.label.toLowerCase() === item.label.toLowerCase() &&
            other.source === item.source
        ) === index
    );
  }

  function getDentalValueForParameter(parameterName, predictions) {
    const name = normalizeParamName(parameterName);

    const rules = [
      {
        parameter: /gum condition|gum health|gingiva|gingivitis/,
        prediction: /gingivitis|gum disease|periodontitis|gingival/,
      },
      {
        parameter: /calculus|tartar/,
        prediction: /calculus|tartar/,
      },
      {
        parameter: /oral ulcer|ulcer|oral lesion|mouth lesion/,
        prediction: /oral ulcer|ulcer|lesion/,
      },
      {
        parameter: /hypodontia|missing teeth|missing tooth/,
        prediction: /hypodontia|missing teeth|missing tooth/,
      },
      {
        parameter: /caries|tooth decay|dental decay/,
        prediction: /caries|tooth decay|dental decay/,
      },
      {
        parameter: /plaque/,
        prediction: /plaque/,
      },
      {
        parameter: /oral hygiene/,
        prediction: /oral hygiene/,
      },
    ];

    const rule = rules.find((item) =>
      item.parameter.test(name)
    );

    if (!rule) return "";

    const match = predictions.find((item) =>
      rule.prediction.test(item.label)
    );

    if (!match) return "";

    return match.confidence !== null
      ? `${match.label} (${match.confidence.toFixed(1)}%)`
      : match.label;
  }


function isNormalAbnormalParam(name) {
  if (!name) return false;
  const n = normalizeParamName(name);
  if (NORMAL_ABNORMAL_EXACT.has(n)) return true;
  // Family fallbacks so spelling variants still get dropdown
  if (n.includes("astigmatism")) return true;
  if (n.includes("duochrome") || n.includes("duo chrome")) return true;
  if ((n.includes("color") || n.includes("colour")) && (n.includes("blind") || n.includes("bindness"))) return true;
  // ECG may come as "ECG ..." — only exact "ecg" or starting with ecg
  if (n === "ecg" || n.startsWith("ecg-") || n.startsWith("ecg ") || n.startsWith("ecg(")) return true;
  // if (n === "waist circumference" || n === "hip circumference" || n === "mid arm circumference") return true;
  // HbA1c variants: "HbA1c", "HbA1c (%)", "HBA1C Test", etc.
  if (n.includes("hba1c") || n.includes("hb a1c") || n.includes("hb-a1c")) return true;
  // Hb variants: exact "Hb" or "Hb ...", "Hb-...", "Hb(...)" — avoid broad includes
  return false;
}

function toNormalAbnormalValue(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "normal") return "Normal";
  if (s === "abnormal") return "Abnormal";
  return "";
}

// Viral markers use Negative / Positive instead of Normal / Abnormal.
function isNegativePositiveParam(name) {
  if (!name) return false;
  const n = normalizeParamName(name);
  if (n === "hcv" || n.startsWith("hcv-") || n.startsWith("hcv ") || n.startsWith("hcv(")) return true;
  if (n.includes("hepatitis c") || n.includes("hep c") || n.includes("hep-c") || n.includes("anti-hcv") || n.includes("anti hcv")) return true;
  if (n === "hbsag" || n.startsWith("hbsag-") || n.startsWith("hbsag ") || n.startsWith("hbsag(")) return true;
  if (n.includes("hepatitis b") || n.includes("hep b") || n.includes("hep-b") || n.includes("surface antigen") || n.includes("australia antigen")) return true;
  if (n === "hbs ag" || n.includes("hbs ag")) return true;
  return false;
}

function toNegativePositiveValue(v) {
  const s = String(v ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  if (s === "negative" || s === "non-reactive" || s === "nonreactive" || s === "non reactive" || s === "normal") return "Negative";
  if (s === "positive" || s === "reactive" || s === "abnormal") return "Positive";
  return "";
}

// New grouped shape: { groups: [{ parameters, subgroups: [{ parameters }] }], ungrouped_parameters }
// plus legacy flat shapes (parameters / test_parameters / results / data).
function flattenTestParameters(data) {
  const out = [];
  const push = (arr) => {
    if (Array.isArray(arr)) arr.forEach((p) => { if (p && typeof p === "object") out.push(p); });
  };
  push(data?.parameters);
  push(data?.test_parameters);
  push(data?.ungrouped_parameters);
  if (Array.isArray(data?.groups)) {
    data.groups.forEach((g) => {
      push(g?.parameters);
      if (Array.isArray(g?.subgroups)) g.subgroups.forEach((sg) => push(sg?.parameters));
    });
  }
  if (out.length === 0) push(data?.results);
  if (out.length === 0 && Array.isArray(data?.data)) push(data.data);
  // Dedupe by id (fallback code) — same parameter listed twice must render once.
  const seen = new Set();
  return out.filter((p) => {
    const key = p.id ?? p.code ?? p.name;
    if (key == null || key === "") return true;
    const k = String(key);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function buildParamSections(data) {
  const clean = (arr) => {
    const list = Array.isArray(arr) ? arr.filter((p) => p && typeof p === "object" && p.is_active !== false) : [];
    list.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    return list;
  };
  const seen = new Set();
  const uniq = (list) => list.filter((p) => {
    const key = p.id ?? p.code ?? p.name;
    if (key == null || key === "") return true;
    const k = String(key);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const sections = [];
  if (Array.isArray(data?.groups) && data.groups.length) {
    data.groups.forEach((g) => {
      const gp = uniq(clean(g?.parameters));
      if (gp.length) sections.push({ title: g?.name || "Group", params: gp });
      if (Array.isArray(g?.subgroups)) {
        g.subgroups.forEach((sg) => {
          const sp = uniq(clean(sg?.parameters));
          if (sp.length) {
            sections.push({
              title: sg?.name ? (g?.name ? `${g.name} · ${sg.name}` : sg.name) : (g?.name || "Subgroup"),
              params: sp,
            });
          }
        });
      }
    });
  }
  const ungrouped = uniq(clean(data?.ungrouped_parameters));
  if (ungrouped.length) sections.push({ title: sections.length ? "Other Parameters" : null, params: ungrouped });
  if (!sections.length) {
    const flat = uniq(clean(data?.parameters?.length ? data.parameters : data?.test_parameters));
    if (flat.length) sections.push({ title: null, params: flat });
    else {
      const fb = uniq(clean(data?.results));
      if (fb.length) sections.push({ title: null, params: fb });
      else if (Array.isArray(data?.data)) {
        const d = uniq(clean(data.data));
        if (d.length) sections.push({ title: null, params: d });
      }
    }
  }
  return sections;
}

export default function MfiServices() {
  const { familyId, memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [family, setFamily] = useState(null);
  const [member, setMember] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [service, setService] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [missingContext, setMissingContext] = useState(false);

  // Dynamic ids resolved from member/pkg objects or query params
  const [numericMemberId, setNumericMemberId] = useState(null);
  const [numericPackageId, setNumericPackageId] = useState(null);

  // API state
  const [parameters, setParameters] = useState([]);
  const [paramSections, setParamSections] = useState([]);
  const [paramMeta, setParamMeta] = useState(null);
  const [loadingParams, setLoadingParams] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [paramsError, setParamsError] = useState(null);
  const [resultsError, setResultsError] = useState(null);

  // formValues: { [parameterId]: { value: string, remarks: string } }
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resultsMap, setResultsMap] = useState({}); // paramId -> result
  // Latest Fitdays OCR result for each parameter ID
  const [ocrResultsMap, setOcrResultsMap] = useState({});
  const [medicalOcrResultsMap, setMedicalOcrResultsMap] = useState({});
  const [medicalOcrReports, setMedicalOcrReports] = useState([]);
  const [medicalReportFile, setMedicalReportFile] = useState(null);
  // const [medicalReportType, setMedicalReportType] = useState("PATHOLOGY");
  const [uploadingMedicalOCR, setUploadingMedicalOCR] = useState(false);
  const [medicalUploadError, setMedicalUploadError] = useState(null);
  const [medicalUploadSuccess, setMedicalUploadSuccess] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [uploadingOCR, setUploadingOCR] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [upperTeethFile, setUpperTeethFile] = useState(null);
  const [lowerTeethFile, setLowerTeethFile] = useState(null);

  const [dentalLoading, setDentalLoading] = useState(false);
  const [dentalError, setDentalError] = useState(null);
  const [dentalSuccess, setDentalSuccess] = useState(null);
  const [dentalResult, setDentalResult] = useState(null);
  // True when the API response contains the Dental Consultation group.
  const [hasDentalConsultationGroup, setHasDentalConsultationGroup] =
    useState(false);

  // Hydrate family/member/pkg/service from navigation state or sessionStorage
  useEffect(() => {
    let fam = location.state?.family || null;
    let mem = location.state?.member || null;
    let p = location.state?.package || null;
    let svc = location.state?.service || null;
    let asg = location.state?.assignment || null;

    if (!fam || !mem) {
      try {
        const f = sessionStorage.getItem("mfi_current_family");
        const m = sessionStorage.getItem("mfi_current_member");
        const pp = sessionStorage.getItem("mfi_current_package");
        const ss = sessionStorage.getItem("mfi_current_service");
        const aa = sessionStorage.getItem("mfi_current_assignment");
        if (f) fam = JSON.parse(f);
        if (m) mem = JSON.parse(m);
        if (pp) p = JSON.parse(pp);
        if (ss) svc = JSON.parse(ss);
        if (aa) asg = JSON.parse(aa);
      } catch {}
    }

    // Validate that member matches route param if provided — do NOT use family head as member
    if (mem && memberId && String(mem.member_id) !== String(memberId)) {
      const found = fam?.members?.find((x) => String(x.member_id) === String(memberId));
      if (found) mem = found;
    }
    if (fam && familyId && String(fam.family_id) !== String(familyId)) {
      // family mismatch — still allow if we have fam, but mark missing if no fam
    }

    if (fam && mem) {
      setFamily(fam);
      setMember(mem);
      setPkg(p);
      setService(svc);
      setAssignment(asg);
    } else {
      // Try query fallback: if no state but we have numeric ids via query, we can still proceed to fetch
      // Check if query has ids and we can show missingContext only if no member/pkg
      const hasQueryIds = searchParams.get("memberId") && searchParams.get("packageId");
      if (!hasQueryIds) {
        // Check if we have at least family/member via session without full context — already handled
        // If still no fam/mem, mark missing
        if (!fam || !mem) setMissingContext(true);
      } else {
        // Allow page to load with query-only ids even if family null (will show minimal header)
        if (fam) setFamily(fam);
        if (mem) setMember(mem);
        if (p) setPkg(p);
        if (svc) setService(svc);
        if (asg) setAssignment(asg);
        if (!fam && !mem) {
          // No context at all but query ids present — allow fetch by ids, show query-based header
          setMissingContext(false);
        }
      }
    }
  }, [familyId, memberId, location.state, searchParams]);

  // Resolve numeric ids from member.id / pkg.id / query params
  useEffect(() => {
    const qMemberId = searchParams.get("memberId") || searchParams.get("family_member_id");
    const qPackageId = searchParams.get("packageId") || searchParams.get("package_id");

    let mId = null;
    let pId = null;

    if (member?.id != null) mId = String(member.id);
    else if (qMemberId) mId = String(qMemberId);
    else if (memberId && /^\d+$/.test(String(memberId))) mId = String(memberId);
    // memberId like MEM-... cannot be used as numeric; must come from member.id or query

    if (pkg?.id != null) pId = String(pkg.id);
    else if (qPackageId) pId = String(qPackageId);
    else if (searchParams.get("packageId")) pId = String(searchParams.get("packageId"));

    // Also try assignment package fallback
    if (!pId && assignment?.package?.id) pId = String(assignment.package.id);
    if (!pId && family?.package?.id) pId = String(family.package.id);

    setNumericMemberId(mId);
    setNumericPackageId(pId);

    if (!mId || !pId) {
      // If we have family/member but missing numeric ids for fetch, show error but not missingContext entirely
      // missingContext is for navigation; numeric missing is an API param error
    }
  }, [member, pkg, assignment, family, searchParams, memberId]);

  // Fetch parameters + results in parallel
  const fetchData = useCallback(async () => {
    if (!numericMemberId || !numericPackageId) return;
    setLoadingParams(true);
    setLoadingResults(true);
    setParamsError(null);
    setResultsError(null);

    const token = localStorage.getItem("token") || localStorage.getItem("access_token");
    if (!token) {
      setParamsError("Not authenticated. Please login again.");
      setResultsError("Not authenticated. Please login again.");
      setLoadingParams(false);
      setLoadingResults(false);
      return;
    }

    try {
      const [paramsRes, resultsRes] = await Promise.allSettled([
        technicianMfiApi.getTestParameters(numericPackageId),
        technicianMfiApi.getMemberTestResults(numericMemberId),
      ]);

      if (paramsRes.status === "fulfilled") {
        const data = paramsRes.value;
        // console.log("getTestParameters response:", data);
        // Detect the Dental Consultation group in the API response.
        const dentalGroupExists = (data?.groups ?? []).some(
          (group) =>
            String(group?.id) === "8" ||
            String(group?.name ?? "").trim().toLowerCase() ===
              "dental consultation"
        );

        setHasDentalConsultationGroup(dentalGroupExists);

        // console.log("[Dental Group Debug]", {
        //   dentalGroupExists,
        //   groups: (data?.groups ?? []).map((group) => ({
        //     id: group?.id,
        //     name: group?.name,
        //   })),
        // });
        const params = flattenTestParameters(data);
        // Respect is_active, sort by display_order
        const active = Array.isArray(params) ? params.filter((p) => p.is_active !== false) : [];
        active.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        setParameters(active);
        setParamSections(buildParamSections(data));
        setParamMeta({ package: data?.package, service: data?.service });
        if (!pkg && data?.package) setPkg(data.package);
        if (!service && data?.service) setService(data.service);
      } else {
        const err = paramsRes.reason;
        const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to load test parameters";
        const status = err?.response?.status;
        if (status === 401) setParamsError("Unauthorized. Please login again.");
        else setParamsError(msg);
        setParameters([]);
        setParamSections([]);
      }

      if (resultsRes.status === "fulfilled") {
        const data = resultsRes.value;

        // -----------------------------------
        // Existing saved TestResult records
        // -----------------------------------
        const results =
          data?.results ||
          data?.test_results ||
          data?.data ||
          [];

        const map = {};

        if (Array.isArray(results)) {
          results.forEach((r) => {
            const pid =
              r.parameter ??
              r.parameter_id ??
              r.parameterId;

            if (pid != null) {
              map[String(pid)] = r;
            }
          });
        }

        setResultsMap(map);

        // -----------------------------------
        // Latest OCR results
        // -----------------------------------
        const latestOcrMap = buildOcrResultsMap(data);

        setOcrResultsMap(latestOcrMap);

         // -----------------------------------
        // New MedicalReportOCR draft workflow
        // -----------------------------------
        const latestMedicalOcrMap =
          buildMedicalOcrResultsMap(data);

        setMedicalOcrResultsMap(latestMedicalOcrMap);

        console.log("Medical OCR reports:", data?.medical_ocr_reports);
        console.log(
          "Medical OCR extracted drafts:",
          data?.medical_report_ocr_results
        );
        console.log("Medical OCR parameter map:", latestMedicalOcrMap);

        // Member information from the results response
        if (!member && data?.member) {
          setMember(data.member);
        }
      } else {
        const err = resultsRes.reason;
        const status = err?.response?.status;
        // 404 for no results is not an error — treat as empty
        if (status === 404) {
          setResultsMap({});
        } else {
          const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to load existing test results";
          if (status === 401) setResultsError("Unauthorized. Please login again.");
          else setResultsError(msg);
          setResultsMap({});
        }
      }
    } finally {
      setLoadingParams(false);
      setLoadingResults(false);
    }
  }, [numericMemberId, numericPackageId, pkg, service, member]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize formValues from parameters + resultsMap (once, or after refresh)
  useEffect(() => {
    // Wait until BOTH API requests have completed.
    // This prevents the form from initializing before OCR data arrives.
    if (loadingParams || loadingResults) return;

    if (parameters.length === 0) return;

    if (!initialized) {
      const next = {};

      parameters.forEach((p) => {
        const parameterId = String(p.id);

        const existing = resultsMap[parameterId];
        const medicalOcrItem = medicalOcrResultsMap[parameterId];
        const ocrItem = ocrResultsMap[parameterId];

        // Priority:
        // 1. Existing saved result
        // 2. Medical report OCR draft
        // 3. Existing OCR workflow
        // 4. Empty field
        const value =
          existing?.value != null
            ? String(existing.value)
            : medicalOcrItem?.value != null
              ? String(medicalOcrItem.value)
              : ocrItem?.value != null
                ? String(ocrItem.value)
                : "";

        next[parameterId] = {
          value,
          remarks:
            existing?.remarks != null
              ? String(existing.remarks)
              : "",
        };
      });

      setFormValues(next);
      setInitialized(true);
    }
  }, [
    parameters,
    resultsMap,
    ocrResultsMap,
    medicalOcrResultsMap,
    initialized,
    loadingParams,
    loadingResults,
  ]);

  // Allow manual refresh to sync from server without losing local edits confirmation — we provide refresh function that resets initialized
  const refreshResults = async () => {
    if (!numericMemberId) return;

    setLoadingResults(true);
    setResultsError(null);

    try {
      const data =
        await technicianMfiApi.getMemberTestResults(
          numericMemberId
        );

      // 1. Existing saved TestResult records
      const results =
        data?.results ||
        data?.test_results ||
        [];

      const map = {};

      if (Array.isArray(results)) {
        results.forEach((r) => {
          const pid =
            r.parameter ??
            r.parameter_id ??
            r.parameterId;

          if (pid != null) {
            map[String(pid)] = r;
          }
        });
      }

      setResultsMap(map);

      // 2. Existing TestResultOCR workflow — keep separate
      const latestOcrMap = buildOcrResultsMap(data);
      setOcrResultsMap(latestOcrMap);

      // 3. New MedicalReportOCR draft workflow
      const latestMedicalOcrMap =
        buildMedicalOcrResultsMap(data);

      setMedicalOcrResultsMap(latestMedicalOcrMap);

      // 4. Refresh form values without overwriting manual edits
      setFormValues((prev) => {
        const next = { ...prev };

        parameters.forEach((p) => {
          const key = String(p.id);

          const existing = map[key];
          const medicalOcrItem = latestMedicalOcrMap[key];
          const ocrItem = latestOcrMap[key];

          const current = next[key] || {
            value: "",
            remarks: "",
          };

          // Saved database results always have the highest priority.
          if (existing) {
            next[key] = {
              ...current,
              value:
                existing.value != null
                  ? String(existing.value)
                  : "",
              remarks:
                existing.remarks != null
                  ? String(existing.remarks)
                  : "",
            };

            return;
          }

          // Preserve any value already entered or edited by the technician.
          if (String(current.value ?? "").trim()) {
            return;
          }

          // Priority 2: Medical Report OCR draft.
          if (
            medicalOcrItem?.value != null &&
            String(medicalOcrItem.value).trim() !== ""
          ) {
            next[key] = {
              ...current,
              value: String(medicalOcrItem.value),
            };

            return;
          }

          // Priority 3: Existing OCR workflow.
          if (
            ocrItem?.value != null &&
            String(ocrItem.value).trim() !== ""
          ) {
            next[key] = {
              ...current,
              value: String(ocrItem.value),
            };
          }
        });

        return next;
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e.message ||
        "Failed to refresh results";

      setResultsError(msg);
    } finally {
      setLoadingResults(false);
    }
  };

  function buildOcrResultsMap(data) {
    const ocrItems = Array.isArray(data?.ocr_results)
      ? data.ocr_results
      : [];

    const reports = Array.isArray(data?.ocr_reports)
      ? data.ocr_reports
      : [];

    // The backend orders reports newest first.
    const latestReportId = reports[0]?.id;

    const latestItems = latestReportId
      ? ocrItems.filter(
          (item) =>
            String(item.ocr_report_id) === String(latestReportId)
        )
      : ocrItems;

    const map = {};

    latestItems.forEach((item) => {
      const parameterId = item.parameter_id ?? item.parameter;

      if (parameterId == null) return;

      const key = String(parameterId);

      // Keep the first value if duplicate entries exist.
      if (!map[key]) {
        map[key] = item;
      }
    });

    return map;
  }

  
  function buildMedicalOcrResultsMap(data) {
    const items = Array.isArray(data?.medical_report_ocr_results)
      ? data.medical_report_ocr_results
      : [];

    const reports = Array.isArray(data?.medical_ocr_reports)
      ? data.medical_ocr_reports
      : [];

    setMedicalOcrReports(reports);

    // Backend should return the newest report first.
    const latestReportId = reports[0]?.id;

    const latestItems =
      latestReportId != null
        ? items.filter((item) => {
            const reportId =
              item.ocr_report_id ??
              item.report_id ??
              item.medical_report_ocr_id;

            // If the item has no report ID, retain it only when
            // there is no report metadata available to filter by.
            if (reportId == null) {
              return reports.length === 0;
            }

            return String(reportId) === String(latestReportId);
          })
        : items;

    const map = {};

    latestItems.forEach((item) => {
      const parameterId =
        item.parameter_id ??
        item.parameter?.id ??
        item.parameter;

      if (
        parameterId == null ||
        typeof parameterId === "object"
      ) {
        return;
      }

      const key = String(parameterId);
      const currentValue = String(item.value ?? "").trim();

      if (
        !map[key] ||
        (
          !String(map[key].value ?? "").trim() &&
          currentValue
        )
      ) {
        map[key] = {
          ...item,
          parameter_id: key,
          value: item.value ?? "",
          is_draft: true,
          source: "MEDICAL_REPORT_OCR",
        };
      }
    });

    return map;
  }


  const handleValueChange = (paramId, field, val) => {
    setFormValues((prev) => ({
      ...prev,
      [String(paramId)]: { ...(prev[String(paramId)] || { value: "", remarks: "" }), [field]: val },
    }));
    if (fieldErrors[String(paramId)]) {
      setFieldErrors((prev) => {
        const n = { ...prev };
        delete n[String(paramId)];
        return n;
      });
    }
  };

  
  const handleOCRUpload = async (e, fileOverride = null) => {
    if (e?.preventDefault) e.preventDefault();

    setUploadError(null);
    setUploadSuccess(null);

    const file = fileOverride || selectedFile;

    if (!file) {
      setUploadError("Please select a report image or document.");
      return;
    }

    if (!numericMemberId) {
      setUploadError("Missing numeric family member ID.");
      return;
    }

    // Prefer the selected service ID, then the package response's service ID.
    // Service 32 is the fallback for your Comprehensive Health Package.
    const serviceId =
      paramMeta?.service?.id ??
      service?.id ??
      32;

    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token");

    if (!token) {
      setUploadError("Not authenticated. Please login again.");
      return;
    }

    const formData = new FormData();
    formData.append("service_id", String(serviceId));
    formData.append("family_member_id", String(numericMemberId));
    formData.append("file", file);

    setUploadingOCR(true);

    try {
      // Upload the file to the OCR API.
      const uploadResponse =
        await technicianMfiApi.uploadTestResultOCR(formData);

      // Fetch the latest server-side saved and OCR results.
      const data =
        await technicianMfiApi.getMemberTestResults(numericMemberId);

      const savedResults = Array.isArray(data?.results)
        ? data.results
        : [];

      const savedMap = {};

      savedResults.forEach((r) => {
        const id = r.parameter_id ?? r.parameter ?? r.parameterId;

        if (id != null) {
          savedMap[String(id)] = r;
        }
      });

      const latestOcrMap = buildOcrResultsMap(data);

      setResultsMap(savedMap);
      setOcrResultsMap(latestOcrMap);

      const latestMedicalOcrMap = buildMedicalOcrResultsMap(data);
      setMedicalOcrResultsMap(latestMedicalOcrMap);

      // Prefill extracted values without overwriting saved results
      // or non-empty values already entered manually.
      setFormValues((prev) => {
        const next = { ...prev };

        parameters.forEach((p) => {
          const key = String(p.id);
          const saved = savedMap[key];
          const ocr = latestOcrMap[key];
          const current = next[key] || {
            value: "",
            remarks: "",
          };

          if (saved) {
            next[key] = {
              value: saved.value != null ? String(saved.value) : "",
              remarks: saved.remarks != null
                ? String(saved.remarks)
                : "",
            };
          } else if (
            ocr?.value != null &&
            !String(current.value ?? "").trim()
          ) {
            next[key] = {
              ...current,
              value: String(ocr.value),
            };
          }
        });

        return next;
      });

      const message =
        uploadResponse?.data?.message ||
        uploadResponse?.data?.detail ||
        uploadResponse?.message ||
        uploadResponse?.detail ||
        "Report uploaded. Retrieved the latest available OCR results.";

      setUploadSuccess(message);
    } catch (err) {
      const data = err?.response?.data;

      const message =
        data?.detail ||
        data?.message ||
        data?.error ||
        err.message ||
        "Failed to upload or retrieve OCR results.";

      setUploadError(message);
    } finally {
      setUploadingOCR(false);
    }
  };

  const handleMedicalReportOCRUpload = async (e, fileOverride = null) => {
    if (e?.preventDefault) e.preventDefault();

    setMedicalUploadError(null);
    setMedicalUploadSuccess(null);

    const file = fileOverride || medicalReportFile;

    if (!file) {
      setMedicalUploadError(
        "Please select a pathology, urine, or other medical report."
      );
      return;
    }

    if (!numericMemberId || !/^\d+$/.test(String(numericMemberId))) {
      setMedicalUploadError("Missing or invalid numeric family member ID.");
      return;
    }

    const serviceId =
      paramMeta?.service?.id ??
      service?.id ??
      32;

    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token");

    if (!token) {
      setMedicalUploadError("Not authenticated. Please login again.");
      return;
    }

    const formData = new FormData();

    // These field names must match MedicalReportOCRUploadView.
    formData.append("family_member_id", String(numericMemberId));
    formData.append("service_id", String(serviceId));
    formData.append("file", file);

    // Add report_type only if the Django endpoint supports this field.
    // For example:
    // formData.append("report_type", medicalReportType);

    setUploadingMedicalOCR(true);

    try {
      const uploadResponse =
        await technicianMfiApi.uploadMedicalReportOCR(formData);

      // Reload server-side saved results and both OCR workflows.
      const data =
        await technicianMfiApi.getMemberTestResults(numericMemberId);

      const savedResults = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.test_results)
          ? data.test_results
          : [];

      const savedMap = {};

      savedResults.forEach((result) => {
        const parameterId =
          result.parameter_id ??
          result.parameter ??
          result.parameterId;

        if (parameterId != null) {
          savedMap[String(parameterId)] = result;
        }
      });

      const latestOcrMap = buildOcrResultsMap(data);
      const latestMedicalMap = buildMedicalOcrResultsMap(data);

      setResultsMap(savedMap);
      setOcrResultsMap(latestOcrMap);
      setMedicalOcrResultsMap(latestMedicalMap);

      // Prefill only fields that are empty, unless a saved server
      // result exists. Do not overwrite technician-entered values.
      setFormValues((prev) => {
        const next = { ...prev };

        parameters.forEach((parameter) => {
          const key = String(parameter.id);
          const saved = savedMap[key];
          const medicalDraft = latestMedicalMap[key];
          const existingOcrDraft = latestOcrMap[key];

          const current = next[key] || {
            value: "",
            remarks: "",
          };

          if (saved) {
            next[key] = {
              value: saved.value != null ? String(saved.value) : "",
              remarks: saved.remarks != null
                ? String(saved.remarks)
                : "",
            };
            return;
          }

          if (String(current.value ?? "").trim()) {
            return;
          }

          const draftValue =
            medicalDraft?.value != null &&
            String(medicalDraft.value).trim()
              ? medicalDraft.value
              : existingOcrDraft?.value;

          if (draftValue != null && String(draftValue).trim()) {
            next[key] = {
              ...current,
              value: String(draftValue),
            };
          }
        });

        return next;
      });

      setMedicalUploadSuccess(
        uploadResponse?.data?.message ||
        uploadResponse?.data?.detail ||
        "Medical report uploaded. Review the extracted draft values before saving."
      );

      setMedicalReportFile(null);
    } catch (err) {
      const data = err?.response?.data;

      setMedicalUploadError(
        data?.detail ||
        data?.message ||
        data?.error ||
        err?.message ||
        "Failed to upload or extract the medical report."
      );
    } finally {
      setUploadingMedicalOCR(false);
    }
  };

  // Determine whether Dental Consultation was explicitly selected.
  // Do not use paramMeta.service here: it represents the package service
  // (ID 32) in your current API response.
  const normalizeServiceName = (value) =>
    String(
      typeof value === "string"
        ? value
        : value?.name ??
          value?.service_name ??
          value?.serviceName ??
          value?.title ??
          ""
    )
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, " ");

  let storedSelectedGroup = null;

  try {
    storedSelectedGroup = JSON.parse(
      sessionStorage.getItem("mfi_selected_group") || "null"
    );
  } catch {
    storedSelectedGroup = null;
  }

  const selectedGroup =
    location.state?.selectedGroup ??
    location.state?.selectedService ??
    storedSelectedGroup ??
    null;

  // TEMPORARY TEST:
  // The API confirms that Dental Consultation exists in this package.
  const isDentalConsultation = hasDentalConsultationGroup;

  // console.log("[Dental AI Debug]", {
  //   selectedGroup,
  //   hasDentalConsultationGroup,
  //   isDentalConsultation,
  // });


  // Submit upper and lower teeth images for Dental AI screening  
  const handleDentalScreening = async (e) => {
    e.preventDefault();

    setDentalError(null);
    setDentalSuccess(null);
    setDentalResult(null);

    // Validate member ID
    if (
      !numericMemberId ||
      !/^\d+$/.test(String(numericMemberId)) ||
      Number(numericMemberId) <= 0
    ) {
      setDentalError(
        "Invalid numeric member ID. Please reselect the family member."
      );
      return;
    }

    // Validate upper image
    if (!(upperTeethFile instanceof File) || upperTeethFile.size === 0) {
      setDentalError("Please select the upper teeth image again.");
      return;
    }

    // Validate lower image
    if (!(lowerTeethFile instanceof File) || lowerTeethFile.size === 0) {
      setDentalError("Please select the lower teeth image again.");
      return;
    }

    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token");

    if (!token) {
      setDentalError("Not authenticated. Please login again.");
      return;
    }

    
    const formData = new FormData();

    // Send the FamilyMember database primary key.
    formData.append(
      "family_member_id",
      String(numericMemberId)
    );

    // These image names must match the Django backend.
    formData.append("upper_image", upperTeethFile);
    formData.append("lower_image", lowerTeethFile);

    // Verify the exact values being submitted.
    // console.log("Dental AI upload verification:", {
    //   family_member_id: formData.get("family_member_id"),
    //   upper_image: formData.get("upper_image")?.name,
    //   lower_image: formData.get("lower_image")?.name,
    //   upper_size: formData.get("upper_image")?.size,
    //   lower_size: formData.get("lower_image")?.size,
    // });


    setDentalLoading(true);

    
  try {
    const response =
      await technicianMfiApi.aiDentalScreening(formData);

    // Store and inspect the original AI response.
    setDentalResult(response);

    console.log("Dental AI API response:", response);

    // Extract disease labels and confidence values.
    const predictions = extractDentalPredictions(response);

    console.log("Extracted dental predictions:", predictions);

    if (predictions.length === 0) {
      setDentalSuccess(
        "AI request completed, but no recognized prediction labels were found. Check the browser console."
      );
      return;
    }

    // Copy matching AI predictions into the Value fields.
    setFormValues((previous) => {
      const next = { ...previous };

      parameters.forEach((parameter) => {
        const parameterId = String(parameter.id);

        // Never overwrite a result already saved on the server.
        if (resultsMap[parameterId]) return;

        const current = next[parameterId] || {
          value: "",
          remarks: "",
        };

        const dentalValue = getDentalValueForParameter(
          parameter.name,
          predictions
        );

        // Fill only when the AI has a matching prediction.
        if (dentalValue) {
          next[parameterId] = {
            ...current,
            value: dentalValue,
          };
        }
      });

      return next;
    });

    setDentalSuccess(
      "Dental AI screening completed. Matching Value fields have been populated. Review the values before saving."
    );
  } catch (err) {
      // console.error(
      //   "Dental AI screening request failed:",
      //   err?.response?.data || err
      // );

      const data = err?.response?.data;

      const message =
        data?.message ||
        data?.detail ||
        data?.error ||
        err?.message ||
        "Dental AI screening failed. Please try again.";

      setDentalError(
        err?.response?.status === 401
          ? "Unauthorized. Please login again."
          : message
      );
    } finally {
      setDentalLoading(false);
    }
  };




  const validate = () => {
    // No parameter is mandatory from the frontend — allow submission
    // even if some or all values are empty. Empty values are skipped
    // when building resultsPayload (see handleSave).
    setFieldErrors({});
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(null);
    if (!numericMemberId || !numericPackageId) {
      const msg = "Missing member or package ID. Please return to dashboard and re-select.";
      setSaveError(msg);
      return;
    }
    const valid = validate();
    if (!valid) {
      return;
    }
    // Build bulk payload dynamically — only include params with a non-empty value.
    const resultsPayload = [];
    parameters.forEach((p) => {
      if (p.is_active === false) return;
      const entry = formValues[String(p.id)];
      const val = entry?.value != null ? String(entry.value).trim() : "";
      const remarks = entry?.remarks != null ? String(entry.remarks).trim() : "";
      // Skip empty values regardless of p.is_required — nothing is mandatory.
      if (val === "") return;
      resultsPayload.push({
        family_member_id: Number(numericMemberId),
        parameter_id: p.id,
        value: val,
        unit: p.unit || "",
        remarks: remarks,
      });
    });

    if (resultsPayload.length === 0) {
      setSaveError("No values to save. Please enter at least one result.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await technicianMfiApi.bulkSaveTestResults(resultsPayload);
      const msg = res?.message || res?.detail || "Results saved successfully";
      setSaveSuccess(msg);
      setSaveError(null);
      // 200 response — go back to dashboard
      navigate("/technician/mfi-dashboard", { state: { toast: msg }, replace: true });
      return;
    } catch (err) {
      const data = err?.response?.data;
      let msg = data?.detail || data?.message || data?.error;
      if (!msg && data && typeof data === "object") {
        // Try to extract first field error
        const firstKey = Object.keys(data)[0];
        if (firstKey) msg = Array.isArray(data[firstKey]) ? data[firstKey][0] : String(data[firstKey]);
      }
      if (!msg) msg = err.message || "Failed to save test results";
      // Do not clear entered values
      setSaveError(msg);
      const status = err?.response?.status;
      if (status === 401) setSaveError("Unauthorized. Please login again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = loadingParams || loadingResults;
  const hasMissingIds = !numericMemberId || !numericPackageId;
  // Grouped display (Vitals → params, Blood Pressure → params, …); fallback to flat list.
  const displaySections = paramSections.length
    ? paramSections
    : (parameters.length ? [{ title: null, params: parameters }] : []);

  const renderParamCard = (p) => {
    const entry = formValues[String(p.id)] || {
      value: "",
      remarks: "",
    };
    const existing = resultsMap[String(p.id)];

    // Medical Report OCR draft for this parameter
    const medicalOcrEntry = medicalOcrResultsMap[String(p.id)];

    // Existing legacy OCR result
    const ocrEntry = ocrResultsMap[String(p.id)];

    const isSaved = !!existing;

    const isMedicalOcrPrefilled =
      !isSaved &&
      !!medicalOcrEntry &&
      medicalOcrEntry.value != null &&
      String(medicalOcrEntry.value).trim() !== "";

    const isOcrPrefilled =
      !isSaved &&
      !isMedicalOcrPrefilled &&
      !!ocrEntry &&
      ocrEntry.value != null &&
      String(ocrEntry.value).trim() !== "";
    const errMsg = fieldErrors[String(p.id)];
    const useSelect = isNormalAbnormalParam(p.name);
    const useNegPosSelect = !useSelect && isNegativePositiveParam(p.name);
    const selectValue = useSelect
      ? toNormalAbnormalValue(entry.value)
      : useNegPosSelect
        ? toNegativePositiveValue(entry.value)
        : entry.value;
    const inputType = (() => {
      const dt = (p.data_type || "").toLowerCase();
      if (dt === "number" || dt === "integer" || dt === "float" || dt === "decimal") return "number";
      return "text";
    })();
    return (
      <div key={p.id} style={{ backgroundColor: COLORS.white, border: `1px solid ${errMsg ? "#fecaca" : COLORS.mediumGrey}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.darkText, display: "flex", alignItems: "center", gap: 6 }}>
              {p.name}
            </div>
          </div>
          {isSaved ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: `${COLORS.grassGreen}18`,
                color: "#15803d",
                padding: "3px 7px",
                borderRadius: 10,
                whiteSpace: "nowrap",
              }}
            >
              {existing.status || "SAVED"}
            </span>
            ) : isMedicalOcrPrefilled ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  backgroundColor: "#eff6ff",
                  color: "#1d4ed8",
                  padding: "3px 7px",
                  borderRadius: 10,
                  whiteSpace: "nowrap",
                }}
              >
                DRAFT
              </span>
          ) : isOcrPrefilled ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: "#fff7ed",
                color: "#c2410c",
                padding: "3px 7px",
                borderRadius: 10,
                whiteSpace: "nowrap",
              }}
            >
              OCR DRAFT
            </span>
          ) : null}
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.darkText }}>Value {p.unit ? `(${p.unit})` : ""}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {useSelect ? (
              <select
                value={selectValue}
                onChange={(e) => handleValueChange(p.id, "value", e.target.value)}
                style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${errMsg ? "#fecaca" : COLORS.mediumGrey}`, fontSize: 13, outline: "none", backgroundColor: errMsg ? "#fef2f2" : COLORS.white, cursor: "pointer" }}
              >
                <option value="">Select Normal / Abnormal</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal">Abnormal</option>
                {/* Preserve legacy non-standard saved value so it is not lost */}
                {entry.value && selectValue === "" ? (
                  <option value={entry.value}>Saved: {entry.value}</option>
                ) : null}
              </select>
            ) : useNegPosSelect ? (
              <select
                value={selectValue}
                onChange={(e) => handleValueChange(p.id, "value", e.target.value)}
                style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${errMsg ? "#fecaca" : COLORS.mediumGrey}`, fontSize: 13, outline: "none", backgroundColor: errMsg ? "#fef2f2" : COLORS.white, cursor: "pointer" }}
              >
                <option value="">Select Negative / Positive</option>
                <option value="Negative">Negative</option>
                <option value="Positive">Positive</option>
                {/* Preserve legacy non-standard saved value so it is not lost */}
                {entry.value && selectValue === "" ? (
                  <option value={entry.value}>Saved: {entry.value}</option>
                ) : null}
              </select>
            ) : (
              <input
                type={inputType}
                step={inputType === "number" ? "any" : undefined}
                value={entry.value}
                onChange={(e) => handleValueChange(p.id, "value", e.target.value)}
                placeholder={existing ? `Existing: ${existing.value}` : `Enter ${p.name.toLowerCase()}`}
                style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${errMsg ? "#fecaca" : COLORS.mediumGrey}`, fontSize: 13, outline: "none", backgroundColor: errMsg ? "#fef2f2" : COLORS.white }}
              />
            )}
            {p.unit && <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.mediumText, whiteSpace: "nowrap" }}>{p.unit}</span>}
          </div>
        </label>



        {errMsg && <div style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>{errMsg}</div>}
        {existing && <div style={{ fontSize: 11, color: COLORS.mediumText }}>Saved: {existing.value} {existing.unit} {existing.remarks ? `· ${existing.remarks}` : ""} · Status {existing.status || "COMPLETED"}</div>}
      </div>
    );
  };

  if (missingContext && !family && !hasMissingIds && parameters.length === 0 && !isLoading) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: COLORS.lightGrey, padding: 24, display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <div style={{ backgroundColor: COLORS.white, border: "1px solid #fecaca", borderRadius: 12, padding: 24, maxWidth: 640, width: "100%", textAlign: "center" }}>
          <p style={{ color: "#dc2626", fontWeight: 700, margin: 0 }}>Missing family/member context</p>
          <p style={{ color: COLORS.mediumText, fontSize: 12, marginTop: 8 }}>The MFI Services page must know family_member_id and package_id. Please return to the family members page and tap “MFI Services” again.</p>
          <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
            <button onClick={() => navigate(-1)} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 16px", cursor: "pointer" }}>Back</button>
            <button onClick={() => navigate("/technician/mfi-dashboard")} style={{ backgroundColor: COLORS.vividPurple, color: COLORS.white, border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontWeight: 600 }}>Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: COLORS.lightGrey }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.white, padding: "14px 20px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, position: "sticky", top: 0, zIndex: 5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => navigate(-1)} style={{ backgroundColor: COLORS.lightGrey, color: COLORS.darkText, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: COLORS.darkText, margin: 0 }}>MFI Services</h1>
        </div>
        <div style={{ fontSize: 11, color: COLORS.mediumText, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {family ? <span>Family <strong style={{ color: COLORS.vividPurple }}>{family.family_id}</strong></span> : <span>Family <strong style={{ color: COLORS.vividPurple }}>{familyId || "—"}</strong></span>}
          <span>·</span>
          {member ? <span>Member <strong style={{ color: COLORS.darkText }}>{member.name}</strong> ({member.member_id || memberId})</span> : <span>Member {memberId || numericMemberId || "—"}</span>}
          {service?.name && <><span>·</span><span style={{ color: COLORS.aquaBlue, fontWeight: 600 }}>{service.name}</span></>}
        </div>
      </div>

      <div style={{ flex: 1, padding: 16, maxWidth: 1100, width: "100%", margin: "0 auto", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Context card — per spec */}
        <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, fontSize: 12 }}>
          <div><div style={{ color: COLORS.lightText, fontSize: 11, fontWeight: 600 }}>Family</div><div style={{ fontWeight: 700, color: COLORS.vividPurple }}>{family?.family_id || familyId || "—"}</div></div>
          <div><div style={{ color: COLORS.lightText, fontSize: 11, fontWeight: 600 }}>Member</div><div style={{ fontWeight: 600 }}>{member?.name || "—"}</div><div style={{ color: COLORS.lightText, fontSize: 11 }}>{member?.member_id || memberId || ""} · {member?.relationship || ""}</div></div>
          <div><div style={{ color: COLORS.lightText, fontSize: 11, fontWeight: 600 }}>Member ID (numeric)</div><div style={{ fontWeight: 600 }}>{numericMemberId || "—"}</div><div style={{ color: COLORS.lightText, fontSize: 11 }}>{memberId || ""}</div></div>
          <div><div style={{ color: COLORS.lightText, fontSize: 11, fontWeight: 600 }}>Package</div><div style={{ fontWeight: 600 }}>{pkg?.name || paramMeta?.package?.name || "—"}</div><div style={{ color: COLORS.mediumText, fontSize: 11 }}>ID {numericPackageId || pkg?.id || "—"} · {paramMeta?.service?.name || service?.name || ""}</div></div>
        </div>

        {hasMissingIds && !isLoading && (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, color: "#dc2626", fontSize: 13 }}>
            Missing package or member ID. Package: {numericPackageId || "—"}, Member: {numericMemberId || "—"}. Please return to dashboard and re-select via “MFI Services”. If you refreshed, ensure URL contains ?packageId &amp; memberId.
          </div>
        )}

        {isLoading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 32, backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}` }}>
            <div style={{ width: 32, height: 32, border: `4px solid ${COLORS.mediumGrey}`, borderTop: `4px solid ${COLORS.vividPurple}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <p style={{ marginTop: 10, color: COLORS.mediumText, fontSize: 13 }}>{loadingParams && loadingResults ? "Loading test parameters and results..." : loadingParams ? "Loading test parameters..." : "Loading existing results..."}</p>
          </div>
        )}

        {!isLoading && paramsError && (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, color: "#dc2626", fontSize: 13 }}>
            Failed to load test parameters: {paramsError}
          </div>
        )}
        {!isLoading && resultsError && (
          <div style={{ backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 12, color: "#92400e", fontSize: 13 }}>
            Failed to load existing results: {resultsError}
          </div>
        )}
        {/* BMR / Health Report OCR Upload */}
        {!isLoading && !hasMissingIds && (
          <div
            style={{
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.mediumGrey}`,
              borderRadius: 12,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: COLORS.darkText,
                  margin: "0 0 6px",
                }}
              >
                Upload BMR / Health Report
              </h3>

              <p
                style={{
                  fontSize: 12,
                  color: COLORS.mediumText,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Choose a report photo or PDF — it will upload and extract
                automatically. Extracted values prefill matching test
                parameters; review them before saving.
              </p>
            </div>

            <form
              onSubmit={handleOCRUpload}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <input
                type="file"
                accept="image/*,.pdf"
                disabled={uploadingOCR}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setSelectedFile(file);
                  setUploadError(null);
                  setUploadSuccess(null);
                  // Direct upload: extract and upload immediately after selection.
                  if (file) handleOCRUpload(null, file);
                  // Allow selecting the same file again for re-upload.
                  e.target.value = "";
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />

              {selectedFile && (
                <div
                  style={{
                    fontSize: 12,
                    color: COLORS.mediumText,
                    overflowWrap: "anywhere",
                  }}
                >
                  Selected file: {selectedFile.name}
                </div>
              )}

              {uploadError && (
                <div
                  role="alert"
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    border: "1px solid #fecaca",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 12,
                  }}
                >
                  {uploadError}
                </div>
              )}

              {uploadSuccess && (
                <div
                  role="status"
                  style={{
                    backgroundColor: "#f0fdf4",
                    color: "#15803d",
                    border: "1px solid #bbf7d0",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 12,
                  }}
                >
                  {uploadSuccess}
                </div>
              )}

              {uploadingOCR && (
                <div
                  role="status"
                  style={{
                    backgroundColor: "#eff6ff",
                    color: "#1d4ed8",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Uploading and extracting results...
                </div>
              )}
            </form>
          </div>
        )}

        
        {/* Medical Reports OCR Upload — separate from Test Results OCR */}
        {!isLoading && !hasMissingIds && (
          <div
            style={{
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.mediumGrey}`,
              borderRadius: 12,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: COLORS.darkText,
                  margin: "0 0 6px",
                }}
              >
                Upload Medical Report — Pathology / Urine
              </h3>

              <p
                style={{
                  fontSize: 12,
                  color: COLORS.mediumText,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Choose a pathology, urine, blood test, or other medical
                report as an image or PDF — it will upload and extract
                automatically. Extracted values appear as drafts for review.
              </p>
            </div>

            <form
              onSubmit={handleMedicalReportOCRUpload}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {/* <label
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: COLORS.darkText,
                }}
              >
                Report category

                <select
                  value={medicalReportType}
                  onChange={(e) => setMedicalReportType(e.target.value)}
                  disabled={uploadingMedicalOCR}
                  style={{
                    padding: 10,
                    border: `1px solid ${COLORS.mediumGrey}`,
                    borderRadius: 8,
                    fontSize: 12,
                    backgroundColor: COLORS.white,
                  }}
                >
                  <option value="PATHOLOGY">Pathology Report</option>
                  <option value="URINE">Urine Report</option>
                  <option value="BLOOD">Blood Test Report</option>
                  <option value="OTHER">Other Medical Report</option>
                </select>
              </label> */}

              <input
                type="file"
                accept="image/*,.pdf"
                disabled={uploadingMedicalOCR}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setMedicalReportFile(file);
                  setMedicalUploadError(null);
                  setMedicalUploadSuccess(null);
                  // Direct upload: extract and upload immediately after selection.
                  if (file) handleMedicalReportOCRUpload(null, file);
                  // Allow selecting the same file again for re-upload.
                  e.target.value = "";
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />

              {medicalReportFile && (
                <div
                  style={{
                    fontSize: 12,
                    color: COLORS.mediumText,
                    overflowWrap: "anywhere",
                  }}
                >
                  Selected file: {medicalReportFile.name}
                </div>
              )}

              {medicalUploadError && (
                <div
                  role="alert"
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    border: "1px solid #fecaca",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 12,
                  }}
                >
                  {medicalUploadError}
                </div>
              )}

              {medicalUploadSuccess && (
                <div
                  role="status"
                  style={{
                    backgroundColor: "#f0fdf4",
                    color: "#15803d",
                    border: "1px solid #bbf7d0",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 12,
                  }}
                >
                  {medicalUploadSuccess}
                </div>
              )}

              {uploadingMedicalOCR && (
                <div
                  role="status"
                  style={{
                    backgroundColor: "#eff6ff",
                    color: "#1d4ed8",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Uploading and extracting results...
                </div>
              )}
            </form>
          </div>
        )}


      {/* Dental AI Screening */}
      {isDentalConsultation && (
        <div
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.mediumGrey}`,
            borderRadius: 12,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div>
            <h3
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: COLORS.darkText,
                margin: "0 0 6px",
              }}
            >
              Dental AI Screening
            </h3>

            <p
              style={{
                fontSize: 12,
                color: COLORS.mediumText,
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Upload clear images of the upper and lower teeth to run
              the dental AI screening for the selected family member.
            </p>

            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: COLORS.mediumText,
              }}
            >
              Member database ID:{" "}
              <strong style={{ color: COLORS.darkText }}>
                {numericMemberId || "—"}
              </strong>
            </div>
          </div>

          <form
            onSubmit={handleDentalScreening}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {/* Upper teeth image */}
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: COLORS.darkText,
                }}
              >
                Upper Teeth Image
              </span>

              <input
                type="file"
                accept="image/*"
                required
                disabled={dentalLoading}
                onChange={(e) => {
                  setUpperTeethFile(e.target.files?.[0] || null);
                  setDentalError(null);
                  setDentalSuccess(null);
                  setDentalResult(null);
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />

              {upperTeethFile && (
                <span
                  style={{
                    fontSize: 11,
                    color: COLORS.mediumText,
                    overflowWrap: "anywhere",
                  }}
                >
                  Selected: {upperTeethFile.name}
                </span>
              )}
            </label>

            {/* Lower teeth image */}
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: COLORS.darkText,
                }}
              >
                Lower Teeth Image
              </span>

              <input
                type="file"
                accept="image/*"
                required
                disabled={dentalLoading}
                onChange={(e) => {
                  setLowerTeethFile(e.target.files?.[0] || null);
                  setDentalError(null);
                  setDentalSuccess(null);
                  setDentalResult(null);
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />

              {lowerTeethFile && (
                <span
                  style={{
                    fontSize: 11,
                    color: COLORS.mediumText,
                    overflowWrap: "anywhere",
                  }}
                >
                  Selected: {lowerTeethFile.name}
                </span>
              )}
            </label>

            {/* Error message */}
            {dentalError && (
              <div
                role="alert"
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#dc2626",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12,
                  overflowWrap: "anywhere",
                }}
              >
                {dentalError}
              </div>
            )}

            {/* Success message */}
            {dentalSuccess && (
              <div
                role="status"
                style={{
                  backgroundColor: "#f0fdf4",
                  color: "#15803d",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12,
                }}
              >
                {dentalSuccess}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={
                dentalLoading ||
                !upperTeethFile ||
                !lowerTeethFile ||
                !numericMemberId
              }
              style={{
                backgroundColor:
                  dentalLoading ||
                  !upperTeethFile ||
                  !lowerTeethFile ||
                  !numericMemberId
                    ? COLORS.mediumGrey
                    : COLORS.aquaBlue,
                color: COLORS.white,
                border: "none",
                borderRadius: 7,
                padding: "11px 16px",
                cursor:
                  dentalLoading ||
                  !upperTeethFile ||
                  !lowerTeethFile ||
                  !numericMemberId
                    ? "not-allowed"
                    : "pointer",
                fontSize: 13,
                fontWeight: 700,
                alignSelf: "flex-start",
              }}
            >
              {dentalLoading
                ? "Analyzing Dental Images..."
                : "Run Dental AI Screening"}
            </button>
          </form>

          {/* AI response
          {dentalResult != null && (
            <div
              style={{
                border: `1px solid ${COLORS.mediumGrey}`,
                borderRadius: 8,
                padding: 12,
                backgroundColor: "#f9fafb",
              }}
            >
              <h4
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: COLORS.darkText,
                  margin: "0 0 10px",
                }}
              >
                Dental AI Response
              </h4>

              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: COLORS.darkText,
                  fontFamily: "monospace",
                }}
              >
                {JSON.stringify(dentalResult, null, 2)}
              </pre>
            </div>
          )} */}
        </div>
      )}


        {!isLoading && !paramsError && parameters.length === 0 && !hasMissingIds && (
          <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
            <p style={{ color: COLORS.mediumText, fontSize: 13, fontWeight: 600, margin: 0 }}>No test parameters for this package</p>
            <p style={{ color: COLORS.lightText, fontSize: 12, margin: "6px 0 0 0" }}>Package {numericPackageId} has no parameters configured. Contact admin.</p>
          </div>
        )}

        {!isLoading && parameters.length > 0 && (
          <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {saveError && (
              <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, color: "#dc2626", fontSize: 13 }}>{saveError}</div>
            )}
            {saveSuccess && (
              <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12, color: "#15803d", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.grassGreen, display: "inline-block" }} />
                {saveSuccess}
              </div>
            )}

            <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.darkText }}>Test Parameters <span style={{ fontWeight: 600, color: COLORS.mediumText, fontSize: 11 }}>· {parameters.length} parameters</span></div>
              <div style={{ fontSize: 11, color: COLORS.mediumText }}>{Object.keys(resultsMap).length} already saved · horizontal scroll on mobile</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {displaySections.map((section, idx) => (
                <div key={section.title || `section-${idx}`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {section.title ? (
                    <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.darkText }}>{section.title} <span style={{ fontWeight: 600, color: COLORS.mediumText, fontSize: 11 }}>· {section.params.length} parameters</span></div>
                    </div>
                  ) : null}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                    {section.params.map((p) => renderParamCard(p))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 10, padding: 14, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button type="button" onClick={refreshResults} disabled={loadingResults} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "9px 14px", cursor: loadingResults ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: COLORS.mediumText }}>
                {loadingResults ? "Refreshing..." : "Refresh Results"}
              </button>
              <button type="submit" disabled={submitting || hasMissingIds} style={{ backgroundColor: submitting || hasMissingIds ? COLORS.mediumGrey : COLORS.vividPurple, color: COLORS.white, border: "none", borderRadius: 6, padding: "10px 22px", cursor: submitting || hasMissingIds ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                {submitting && <span style={{ width: 14, height: 14, border: `2px solid ${COLORS.white}`, borderTop: "2px solid transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />}
                {submitting ? "Saving..." : "Save Results"}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
